import { Injectable } from '@angular/core';
import { HttpClient, HttpEventType, HttpEvent } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { environment } from '../../environments/environment';

export interface DownloadItem {
  id: string;
  fileName: string;
  iconPath?: string;
  status: 'downloading' | 'completed' | 'error' | 'cancelled';
  progress: number;
  loaded: number;
  total: number;
  subscription?: Subscription;
}

@Injectable({
  providedIn: 'root'
})
export class DownloadService {
  private brainApiUrl: string = `${environment.apiBaseUrl}/brain`;
  private teamApiUrl: string = `${environment.apiBaseUrl}/team`;
  private downloadsSubject = new BehaviorSubject<DownloadItem[]>([]);
  public downloads$ = this.downloadsSubject.asObservable();

  constructor(private http: HttpClient) {}

  getDownloadFile(teamId: string, path: string){
    return `${environment.apiBaseUrl}/team/${teamId}/download?path=${path}`;
  }
  /**
   * Starts a file download with progress tracking
   * @param teamId - The team ID
   * @param path - The file path
   * @param fileName - The display name for the file
   * @param source - The source type: 'brain' for brain knowledge base or 'team' for team files
   * @param iconPath - Optional icon path for the file type
   */
  downloadFile(teamId: string, path: string, fileName: string, source: 'brain' | 'team' = 'team', iconPath?: string): void {
    const id = this.generateId();
    const baseUrl = source === 'brain' ? this.brainApiUrl : this.teamApiUrl;
    const url = `${baseUrl}/${teamId}/download?path=${path}`;

    const downloadItem: DownloadItem = {
      id,
      fileName: fileName || path,
      iconPath,
      status: 'downloading',
      progress: 0,
      loaded: 0,
      total: 0
    };

    this.addDownload(downloadItem);

    const subscription = this.http.get(url, {
      responseType: 'blob',
      reportProgress: true,
      observe: 'events'
    }).subscribe({
      next: (event: HttpEvent<Blob>) => {
        if (event.type === HttpEventType.DownloadProgress) {
          const progress = event.total
            ? Math.round((event.loaded / event.total) * 100)
            : 0;
          this.updateDownload(id, {
            progress,
            loaded: event.loaded,
            total: event.total || 0
          });
        } else if (event.type === HttpEventType.Response) {
          const blob = event.body;
          if (blob) {
            this.triggerBrowserDownload(blob, fileName || path);
            this.updateDownload(id, { status: 'completed', progress: 100 });
            this.scheduleAutoRemove(id);
          }
        }
      },
      error: (error) => {
        console.error('Download failed:', error);
        this.updateDownload(id, { status: 'error' });
      }
    });

    // Store subscription for cancellation
    this.updateDownload(id, { subscription });
  }

  /**
   * Cancels an ongoing download
   */
  cancelDownload(id: string): void {
    const downloads = this.downloadsSubject.getValue();
    const item = downloads.find(d => d.id === id);
    if (item?.subscription) {
      item.subscription.unsubscribe();
      this.updateDownload(id, { status: 'cancelled' });
    }
  }

  /**
   * Removes a download from the list
   */
  removeDownload(id: string): void {
    const downloads = this.downloadsSubject.getValue();
    const item = downloads.find(d => d.id === id);
    if (item?.subscription) {
      item.subscription.unsubscribe();
    }
    this.downloadsSubject.next(downloads.filter(d => d.id !== id));
  }

  /**
   * Clears all completed, cancelled, or errored downloads
   */
  clearCompleted(): void {
    const downloads = this.downloadsSubject.getValue();
    this.downloadsSubject.next(
      downloads.filter(d => d.status === 'downloading')
    );
  }

  private addDownload(item: DownloadItem): void {
    const downloads = this.downloadsSubject.getValue();
    this.downloadsSubject.next([item, ...downloads]);
  }

  private updateDownload(id: string, updates: Partial<DownloadItem>): void {
    const downloads = this.downloadsSubject.getValue();
    const index = downloads.findIndex(d => d.id === id);
    if (index !== -1) {
      downloads[index] = { ...downloads[index], ...updates };
      this.downloadsSubject.next([...downloads]);
    }
  }

  private triggerBrowserDownload(blob: Blob, fileName: string): void {
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(blobUrl);
  }

  private scheduleAutoRemove(id: string): void {
    setTimeout(() => {
      const downloads = this.downloadsSubject.getValue();
      const item = downloads.find(d => d.id === id);
      if (item?.status === 'completed') {
        this.removeDownload(id);
      }
    }, 5000); // Auto-remove completed downloads after 5 seconds
  }

  private generateId(): string {
    return `download-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}
